import { BarElement, Chart as ChartJS } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';

import { BarColor, ChartKey } from '@features/campaigns/performance/shared/components/HorizontalBarChart/consts.ts';
import { HorizontalBarChartData } from '@features/campaigns/performance/shared/components/HorizontalBarChart/HorizontalBarChart.types.ts';
import {
  generateChartData,
  generateChartOptions,
} from '@features/campaigns/performance/shared/components/HorizontalBarChart/utils.ts';

import styles from './HorizontalBarChart.module.scss';

ChartJS.register(ChartDataLabels, BarElement);

interface HorizontalBarChartProps {
  data: HorizontalBarChartData;
  barColor: BarColor;
  dataTestId: string;
  chartKey: ChartKey;
}

function HorizontalBarChart({ data, barColor, dataTestId, chartKey }: HorizontalBarChartProps) {
  const barAndGapHeight = 45;
  const legendHeight = 60;
  const maxHeightOfChartWrapper = 21 * barAndGapHeight;
  const isScrollableChart = data.labels.length * barAndGapHeight > maxHeightOfChartWrapper;
  const scrollbarWidth = 6;

  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number | undefined>(undefined);
  const [isScrollAtBottom, setIsScrollAtBottom] = useState(false);
  const [previousChartKey, setPreviousChartKey] = useState(chartKey);

  if (previousChartKey !== chartKey) {
    setPreviousChartKey(chartKey);
    setIsScrollAtBottom(false);
  }

  const shouldDisplayChartShadow = isScrollableChart && !isScrollAtBottom;

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLDivElement;

    const isAtBottom = target.scrollHeight - target.scrollTop === target.clientHeight;

    setIsScrollAtBottom(isAtBottom);
  };

  useEffect(() => {
    const element = chartWrapperRef.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver(() => {
      const { clientWidth } = element;
      if (!clientWidth) {
        return;
      }
      setChartWidth(isScrollableChart ? clientWidth + scrollbarWidth : clientWidth);
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [isScrollableChart]);

  return (
    <div
      ref={chartWrapperRef}
      onScroll={(event) => (isScrollableChart ? handleScroll(event) : null)}
      style={{
        height: isScrollableChart ? maxHeightOfChartWrapper : data.labels.length * barAndGapHeight + legendHeight,
      }}
      className={cn(styles.chartWrapper, { [styles.shadow]: shouldDisplayChartShadow })}
    >
      {chartWidth && (
        <Bar
          data={generateChartData(data, barColor)}
          options={generateChartOptions(data, barColor, !isScrollableChart)}
          height={data.labels.length * barAndGapHeight + legendHeight}
          width={chartWidth}
          data-testid={`${dataTestId}-chart`}
          key={chartKey}
        />
      )}
    </div>
  );
}

export default HorizontalBarChart;
